import { Tab, Tabs } from "@flat-ui";
import { useMemo } from "react";
import { Box } from "@mui-wrapper";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { OpenTabEvents } from "@utils/track-event/track-event-list";
import { TabItemProps } from "@components/tabs/tab-types";
import { useNavigate, useLocation } from "react-router-dom";
import { RouteParamValues } from "@router/route-params";

interface Props<K extends RouteParamValues> {
  /** List of all tabs to be shown */
  tabs: TabItemProps<K>[];

  /** Zero-based index of the tab (in tabs) that should be opened/selected per default */
  selectedTab?: K;

  /** Shown between the tabs and their content, e.g. an info bar about the page */
  header?: React.ReactNode;

  /**
   * The name of the event to track opening tab events.
   * In the same page all opening of a tab is tracked with the same event name,
   * and the tab name is sent as a prop.
   */
  trackingEventPageName?: OpenTabEvents;
}

/** Manages displaying the tabs and their selection */
export function TabComponent<K extends RouteParamValues>({
  tabs,
  selectedTab,
  header,
  trackingEventPageName,
}: Props<K>): React.ReactNode {
  const { trackEvent } = useTrackEvent();
  const navigate = useNavigate();
  const location = useLocation();

  const selectedTabItem = useMemo(() => {
    return tabs.find(({ route }) => route === selectedTab);
  }, [selectedTab, tabs]);

  if (!selectedTabItem) {
    return null;
  }

  function handleTabChange(newTabRoute: K): void {
    const newPath = location.pathname.replace(/[^/]+$/, newTabRoute);
    // eslint-disable-next-line @typescript-eslint/naming-convention -- From external package
    navigate(newPath, { replace: true });
  }

  return (
    <>
      <Box sx={{ px: 6 }}>
        <Tabs value={selectedTab}>
          {tabs.map((tab, index) => {
            if (tab.isHidden) {
              return null;
            }
            return (
              <Tab
                disableRipple
                key={index}
                label={tab.label}
                value={tab.route}
                onClick={() => {
                  handleTabChange(tab.route);
                  if (trackingEventPageName) {
                    trackEvent({
                      name: trackingEventPageName,
                      props: {
                        tab: tab.label,
                      },
                    });
                  }
                }}
              />
            );
          })}
        </Tabs>
      </Box>

      {header && <Box sx={{ px: 6, mt: 1.5, mb: 2 }}>{header}</Box>}

      {/* Content to show for each individual tab */}
      <Box
        key={selectedTabItem.route}
        sx={{
          flexGrow: 1,
          height: "300px",
          overflow: "auto",
          // The same side padding as the tabs and the header above, so the content lines up with them
          px: 6,
        }}
        role="tabpanel"
        hidden={selectedTabItem.route !== selectedTab}
      >
        {selectedTabItem.content}
      </Box>
    </>
  );
}
